import React from "react";
import { Box, Drawer, Radio, Typography, useTheme } from "@mui/material";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { setReduxSelectedParticipant } from "redux/appSlice";
import { RequestParticipant } from "models/interfaces";

interface MobileChooseSignerSheetProps {
  open: boolean;
  onClose: () => void;
}

const getInitials = (name?: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const MobileChooseSignerSheet: React.FC<MobileChooseSignerSheetProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const m = theme.palette.mobile;
  const dispatch = useAppDispatch();
  const { signRequest, selectedParticipant } = useAppSelector(
    (s: RootState) => s.app,
  );

  const participants: RequestParticipant[] = (signRequest.signers || []).filter(
    (s: RequestParticipant) => !s.cc,
  );

  const selectSigner = (p: RequestParticipant) => {
    dispatch(setReduxSelectedParticipant(p));
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { px: 2, pb: 3 },
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "choose-signer-sheet-title",
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 1.5 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: m.grey[50] }}
        />
      </Box>

      {/* Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography
          id="choose-signer-sheet-title"
          component="h2"
          sx={{ fontSize: "1.125rem", fontWeight: 700, color: m.base[900] }}
        >
          Signers
        </Typography>
        <Typography
          sx={{ fontSize: "0.875rem", fontWeight: 600, color: m.grey[400] }}
        >
          {participants.length} Signer
        </Typography>
      </Box>

      <Typography
        sx={{ fontSize: "0.75rem", color: m.grey[400], fontWeight: 500, mb: 2 }}
      >
        Each signer’s fields get a unique color
      </Typography>

      {/* Signer list */}
      {participants.map((p: RequestParticipant, index: number) => {
        const isSelected = p.id === selectedParticipant?.id;
        return (
          <Box
            key={p.id ?? index}
            onClick={() => selectSigner(p)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              border: `1px solid ${m.surface[100]}`,
              borderRadius: "14px",
              p: 1.5,
              mb: 1.5,
              cursor: "pointer",
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: p.color || m.base[300],
                color: m.surface.white,
                fontSize: "0.8125rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {getInitials(p.name)}
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: m.base[900],
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {p.name || "Unnamed signer"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 500,
                  color: m.grey[400],
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {p.email}
              </Typography>
            </Box>
            <Radio
              checked={isSelected}
              onChange={() => selectSigner(p)}
              onClick={(e) => e.stopPropagation()}
              name="mobile-signer-select"
              inputProps={{
                "aria-label": `Select ${p.name || "unnamed signer"}`,
              }}
              sx={{
                p: 0,
                color: m.grey[50],
                "&.Mui-checked": { color: m.ePurple[500] },
              }}
            />
          </Box>
        );
      })}
    </Drawer>
  );
};

export default MobileChooseSignerSheet;
